import ArchiveDealDialog from '@/components/archive/archive-deal-dialog.tsx';
import LenderDialog from '@/components/deal/lenders/lender-dialog.tsx';
import CreateTradeDialog from '@/components/trade/create/create-trade-in-appraisal-dialog';
import HotkeyDialogBoxes from '@/components/ux/all-list-and-board/hotkey-dialog-boxes.tsx';
import type { HotkeyDialogBox } from '@/components/ux/all-list-and-board/use-all-list-and-board-hotkeys.ts';
import { useDealActions } from '@/hooks/use-deal-actions.tsx';
import { HOTKEYS } from '@/lib/hotkeys';
import { Deal } from '@/models/registry';
import { Copy } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { PDKAddTradeInIcon } from '../../ui/pdk-icons';
import HotKeyListener from '../../ux/hot-key-listener';
import DealMenuButton from './deal-menu-button';

const DealMenu = observer(
  ({
    deal,
    withHokeyListener = false,
  }: {
    deal: Deal;
    withHokeyListener?: boolean;
  }) => {
    const [tradeInDialogIsOpen, setTradeInDialogIsOpen] = useState(false);
    const [openDialog, setOpenDialog] = useState<HotkeyDialogBox | null>(null);
    const [createLenderDialogIsOpen, setCreateLenderDialogIsOpen] =
      useState(false);
    const [open, setOpen] = useState(false);
    const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);

    const { dealActions, handleCreateLender } = useDealActions({
      deal,
      dealShortId: deal.short_id ?? '',
      setOpenDialog,
      setCreateLenderDialogIsOpen,
    });

    return (
      <div>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <div>
              <DealMenuButton onClick={() => setOpen(true)} />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side="bottom"
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Trade-In Actions
            </DropdownMenuLabel>
            <DropdownMenuItem>
              <div
                className="flex flex-row gap-2 w-full"
                onClick={(e) => {
                  // BUG NOTE: Radix has this bug where the whole application will freeze if you open the dialog
                  // within a dropdown menu... this is a voodoo workaround
                  // https://github.com/radix-ui/themes/issues/155#issuecomment-2476116687
                  setTimeout(() => {
                    e.stopPropagation();
                    e.preventDefault();
                    setTradeInDialogIsOpen(true);
                  }, 0);
                }}
              >
                <PDKAddTradeInIcon className="size-3" />
                <p className="text-xs">Add Trade-In</p>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={() => {
                navigator.clipboard.writeText(
                  deal?.vehicle_listing?.vehicle?.vin || '',
                );
              }}
              className="flex flex-row gap-2 "
            >
              <Copy className="size-3" />
              <div className="flex flex-col text-nowrap truncate">
                <p className="text-xs">Copy Vin</p>
                {/* <p className="text-xs text-muted-foreground">{tradeIn.vin}</p> */}
              </div>
              <DropdownMenuShortcut>⌘3</DropdownMenuShortcut>
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            {dealActions.map((action) => (
              <DropdownMenuItem
                className="flex flex-row gap-2 text-xs"
                key={action.value}
                onClick={action.onClick}
              >
                {action.icon}
                {action.label}
              </DropdownMenuItem>
            ))}

            {/*TODO -- uncomment when potential delta race condition between archivedealstore and dealstore is fixed */}
            {/*<DropdownMenuSeparator />*/}
            {/*{isArchiveEnabled && (*/}
            {/*  <DropdownMenuItem*/}
            {/*    onClick={(e) => {*/}
            {/*      e.preventDefault();*/}
            {/*      e.stopPropagation();*/}
            {/*      openArchiveDialogFromMenu(setOpen, setConfirmArchiveOpen);*/}
            {/*    }}*/}
            {/*    className="flex flex-row gap-2 "*/}
            {/*  >*/}
            {/*    <PDKArchiveIcon className="size-3" />*/}
            {/*    <div className="flex flex-col text-nowrap truncate">*/}
            {/*      <p className="text-xs">Archive Deal</p>*/}
            {/*    </div>*/}
            {/*  </DropdownMenuItem>*/}
            {/*)}*/}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* <TradeCreateButton
        dealId={dealShortId}
        dialogIsOpenProp={tradeInDialogIsOpen}
      /> */}

        <CreateTradeDialog
          open={tradeInDialogIsOpen}
          onOpenChange={setTradeInDialogIsOpen}
          initialDealId={deal.id}
        />

        {withHokeyListener && (
          <HotKeyListener
            hotkeys={HOTKEYS.DEAL_DEAL_MENU}
            onHotkeyPressed={() => {
              setOpen(true);
            }}
          />
        )}
        <HotkeyDialogBoxes
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
          selectedDeal={deal}
        />
        {handleCreateLender && (
          <LenderDialog
            initialName={''}
            initialEmail={''}
            initialPhone={''}
            initialLenderType={'bank'}
            onClickCta={handleCreateLender}
            ctaText={'Create Lender'}
            onOpen={() => setCreateLenderDialogIsOpen(true)}
            onClose={() => setCreateLenderDialogIsOpen(false)}
            dialogIsOpen={createLenderDialogIsOpen}
          />
        )}
        <ArchiveDealDialog
          open={confirmArchiveOpen}
          onOpenChange={setConfirmArchiveOpen}
          dealId={deal.id}
          dealShortId={deal.short_id ?? ''}
        />
      </div>
    );
  },
);

export default DealMenu;
